import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// ── API ───────────────────────────────────────────────────────────────────────
function getApiBase() {
  const env = process.env.REACT_APP_ENV;
  if (env === 'LOCAL')   return 'http://localhost:8888/Website/front-end/public/api';
  if (env === 'STAGING') return 'https://staging.belvoircastle.com/api';
  return 'https://www.belvoircastle.com/api';
}

// ── Styled components (match Security Centre card system) ─────────────────────

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const ToolbarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const ToolbarLabel = styled.span`
  font-size: 0.9rem;
  font-weight: 600;
  color: ${p => p.theme.colors.secondary};
`;

const RangeButton = styled.button`
  padding: 0.4rem 1.1rem;
  border-radius: 8px;
  border: 2px solid ${p => p.active ? p.theme.colors.primary : 'rgba(16,29,64,0.15)'};
  background: ${p => p.active ? p.theme.colors.primary : 'white'};
  color: ${p => p.active ? 'white' : p.theme.colors.primary};
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: ${p => p.theme.colors.primary};
    background: ${p => p.active ? p.theme.colors.secondary : 'rgba(16,29,64,0.05)'};
  }
`;

const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 1rem;
`;

const KpiCard = styled.div`
  background: white;
  border: 1px solid rgba(16,29,64,0.08);
  border-radius: 12px;
  padding: 1.1rem 1.25rem 1rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
`;

const KpiLabel = styled.p`
  margin: 0 0 0.3rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: ${p => p.theme.colors.secondary};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const KpiValue = styled.p`
  margin: 0;
  font-size: 1.9rem;
  font-weight: 700;
  color: ${p => p.theme.colors.primary};
  line-height: 1;
`;

const KpiSub = styled.p`
  margin: 0.25rem 0 0;
  font-size: 0.78rem;
  color: ${p => p.theme.colors.secondary};
  opacity: 0.7;
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled.div`
  background: white;
  border: 1px solid rgba(16,29,64,0.08);
  border-radius: 14px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
`;

const ChartTitle = styled.h3`
  margin: 0 0 1.25rem;
  font-size: 0.95rem;
  font-weight: 700;
  color: ${p => p.theme.colors.primary};
`;

const TableCard = styled(ChartCard)`
  grid-column: 1 / -1;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
`;

const Th = styled.th`
  text-align: left;
  padding: 0.6rem 0.75rem;
  background: ${p => p.theme.colors.offWhite};
  color: ${p => p.theme.colors.secondary};
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-bottom: 1px solid rgba(16,29,64,0.1);

  &:first-child { border-radius: 8px 0 0 8px; }
  &:last-child  { border-radius: 0 8px 8px 0; }
`;

const Td = styled.td`
  padding: 0.65rem 0.75rem;
  border-bottom: 1px solid rgba(16,29,64,0.06);
  color: ${p => p.theme.colors.primary};
  vertical-align: middle;
`;

const TypeBadge = styled.span`
  display: inline-block;
  padding: 0.2rem 0.65rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${p => p.type === 'membership_signup'
    ? 'rgba(157,109,73,0.12)'
    : 'rgba(16,29,64,0.08)'};
  color: ${p => p.type === 'membership_signup'
    ? '#9d6d49'
    : '#101d40'};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: ${p => p.theme.colors.secondary};
  opacity: 0.6;
  font-size: 0.95rem;
`;

const ErrorBox = styled.div`
  background: #fff5f5;
  border: 1px solid #feb2b2;
  border-radius: 10px;
  padding: 1rem 1.25rem;
  color: #c53030;
  font-size: 0.9rem;
`;

const Spinner = styled.div`
  text-align: center;
  padding: 3rem;
  color: ${p => p.theme.colors.secondary};
  font-size: 0.95rem;
`;

// ── Chart config helpers ──────────────────────────────────────────────────────
const NAVY   = '#101d40';
const ACCENT = '#9d6d49';
const TEAL   = '#2a7f8f';

const lineDefaults = {
  tension: 0.35,
  pointRadius: 2,
  pointHoverRadius: 4,
  borderWidth: 2,
  fill: true,
};

const chartOptions = (title) => ({
  responsive: true,
  plugins: {
    legend: { display: true, labels: { boxWidth: 12, font: { size: 11 } } },
    title:  { display: false },
    tooltip: { mode: 'index', intersect: false },
  },
  scales: {
    x: { grid: { display: false }, ticks: { font: { size: 10 } } },
    y: { beginAtZero: true, ticks: { font: { size: 10 } } },
  },
});

// ── Build date series (fill gaps) ─────────────────────────────────────────────
function buildDateRange(days) {
  const dates = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    dates.push(d.toISOString().slice(0, 10));
  }
  return dates;
}

function fillSeries(rows, dateField, valueField, dates) {
  const map = {};
  rows.forEach(r => { map[r[dateField]] = Number(r[valueField] ?? 0); });
  return dates.map(d => map[d] ?? 0);
}

function formatCurrency(val) {
  if (val === null || val === undefined) return '—';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 0 }).format(val);
}

function formatDate(str) {
  if (!str) return '—';
  return new Date(str).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatDateShort(str) {
  if (!str) return '';
  const d = new Date(str);
  return `${d.getDate()} ${d.toLocaleString('en-GB', { month: 'short' })}`;
}

// ── Component ─────────────────────────────────────────────────────────────────
const MarketingAnalytics = () => {
  const [days, setDays]       = useState(30);
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const fetchData = useCallback(async (d) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${getApiBase()}/marketing-analytics.php?action=report&days=${d}`,
        { headers: { 'X-Admin-Request': '1' } }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Unknown error');
      setData(json);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(days); }, [days, fetchData]);

  const handleRange = (d) => { setDays(d); };

  // ── Build chart datasets ──────────────────────────────────────────────────
  let sessionChart    = null;
  let conversionChart = null;
  let sourceChart     = null;
  let firstTouchChart = null;

  if (data) {
    const dates = buildDateRange(days);
    const labels = dates.map(formatDateShort);

    // Sessions over time
    sessionChart = {
      labels,
      datasets: [
        {
          ...lineDefaults,
          label: 'Sessions',
          data:  fillSeries(data.daily_sessions, 'date', 'sessions', dates),
          borderColor: NAVY,
          backgroundColor: 'rgba(16,29,64,0.07)',
        },
        {
          ...lineDefaults,
          label: 'Unique Visitors',
          data:  fillSeries(data.daily_sessions, 'date', 'visitors', dates),
          borderColor: TEAL,
          backgroundColor: 'rgba(42,127,143,0.06)',
        },
      ],
    };

    // Conversions over time
    const ticketByDate = {};
    const memberByDate = {};
    data.daily_conversions.forEach(r => {
      if (r.type === 'ticket_booking')    ticketByDate[r.date] = Number(r.cnt);
      if (r.type === 'membership_signup') memberByDate[r.date] = Number(r.cnt);
    });
    conversionChart = {
      labels,
      datasets: [
        {
          ...lineDefaults,
          label: 'Ticket Bookings',
          data:  dates.map(d => ticketByDate[d] ?? 0),
          borderColor: ACCENT,
          backgroundColor: 'rgba(157,109,73,0.08)',
        },
        {
          ...lineDefaults,
          label: 'Memberships',
          data:  dates.map(d => memberByDate[d] ?? 0),
          borderColor: NAVY,
          backgroundColor: 'rgba(16,29,64,0.07)',
        },
      ],
    };

    // Last-touch traffic sources (bar — top 8)
    const topSources = [...data.sources].slice(0, 8);
    sourceChart = {
      labels: topSources.map(s => s.utm_medium ? `${s.source} / ${s.utm_medium}` : s.source),
      datasets: [{
        label: 'Sessions',
        data:  topSources.map(s => Number(s.sessions)),
        backgroundColor: topSources.map((_, i) =>
          i === 0 ? NAVY : i === 1 ? ACCENT : i === 2 ? TEAL : `rgba(16,29,64,${0.45 - i * 0.04})`
        ),
        borderRadius: 6,
      }],
    };

    // First-touch attribution — where buyers came from originally
    const topFT = [...(data.first_touch_sources || [])].slice(0, 8);
    if (topFT.length > 0) {
      firstTouchChart = {
        labels: topFT.map(s => s.first_touch_medium ? `${s.first_touch_source} / ${s.first_touch_medium}` : s.first_touch_source),
        datasets: [{
          label: 'Conversions',
          data:  topFT.map(s => Number(s.conversions)),
          backgroundColor: topFT.map((_, i) =>
            i === 0 ? ACCENT : i === 1 ? NAVY : i === 2 ? TEAL : `rgba(157,109,73,${0.75 - i * 0.07})`
          ),
          borderRadius: 6,
        }],
      };
    }
  }

  return (
    <Wrapper>
      <ToolbarRow>
        <ToolbarLabel>Period:</ToolbarLabel>
        {[7, 30, 90].map(d => (
          <RangeButton key={d} active={days === d} onClick={() => handleRange(d)}>
            {d} days
          </RangeButton>
        ))}
      </ToolbarRow>

      {loading && <Spinner>Loading analytics data…</Spinner>}
      {error && <ErrorBox>Failed to load data: {error}</ErrorBox>}

      {!loading && !error && data && (
        <>
          {/* KPI row */}
          <KpiGrid>
            <KpiCard>
              <KpiLabel>Unique Visitors</KpiLabel>
              <KpiValue>{data.kpis.unique_visitors.toLocaleString()}</KpiValue>
            </KpiCard>
            <KpiCard>
              <KpiLabel>Sessions</KpiLabel>
              <KpiValue>{data.kpis.total_sessions.toLocaleString()}</KpiValue>
            </KpiCard>
            <KpiCard>
              <KpiLabel>Returning Rate</KpiLabel>
              <KpiValue>{data.kpis.returning_rate}%</KpiValue>
              <KpiSub>{data.kpis.returning_sessions.toLocaleString()} returning sessions</KpiSub>
            </KpiCard>
            <KpiCard>
              <KpiLabel>Ticket Bookings</KpiLabel>
              <KpiValue>{data.kpis.ticket_bookings.toLocaleString()}</KpiValue>
            </KpiCard>
            <KpiCard>
              <KpiLabel>Memberships</KpiLabel>
              <KpiValue>{data.kpis.membership_signups.toLocaleString()}</KpiValue>
            </KpiCard>
            <KpiCard>
              <KpiLabel>Conversion Rate</KpiLabel>
              <KpiValue>{data.kpis.conversion_rate}%</KpiValue>
              <KpiSub>{data.kpis.total_conversions} total</KpiSub>
            </KpiCard>
            <KpiCard>
              <KpiLabel>Attributed Revenue</KpiLabel>
              <KpiValue style={{ fontSize: '1.5rem' }}>{formatCurrency(data.kpis.total_revenue)}</KpiValue>
            </KpiCard>
            <KpiCard>
              <KpiLabel>Avg Pages Before Buying</KpiLabel>
              <KpiValue>{data.kpis.avg_pages_before_conversion}</KpiValue>
              <KpiSub>pages in converting session</KpiSub>
            </KpiCard>
            <KpiCard>
              <KpiLabel>Avg Sessions to Convert</KpiLabel>
              <KpiValue>{data.kpis.avg_sessions_to_convert}</KpiValue>
              <KpiSub>visits before purchase</KpiSub>
            </KpiCard>
            <KpiCard>
              <KpiLabel>Returning Buyers</KpiLabel>
              <KpiValue>{data.kpis.returning_conversion_rate}%</KpiValue>
              <KpiSub>{data.kpis.returning_conversion_count} of {data.kpis.total_conversions} conversions</KpiSub>
            </KpiCard>
          </KpiGrid>

          {/* Charts */}
          <ChartsGrid>
            <ChartCard>
              <ChartTitle>Sessions over time</ChartTitle>
              {sessionChart ? (
                <Line data={sessionChart} options={chartOptions()} height={220} />
              ) : (
                <EmptyState>No session data yet</EmptyState>
              )}
            </ChartCard>

            <ChartCard>
              <ChartTitle>Conversions over time</ChartTitle>
              {conversionChart ? (
                <Line data={conversionChart} options={chartOptions()} height={220} />
              ) : (
                <EmptyState>No conversion data yet</EmptyState>
              )}
            </ChartCard>

            <ChartCard>
              <ChartTitle>Last-touch traffic sources (all sessions)</ChartTitle>
              {sourceChart && sourceChart.labels.length > 0 ? (
                <Bar
                  data={sourceChart}
                  options={{
                    ...chartOptions(),
                    plugins: { ...chartOptions().plugins, legend: { display: false } },
                    indexAxis: 'y',
                  }}
                  height={220}
                />
              ) : (
                <EmptyState>No source data yet</EmptyState>
              )}
            </ChartCard>

            <ChartCard>
              <ChartTitle>First-touch attribution (where buyers originally came from)</ChartTitle>
              {firstTouchChart ? (
                <Bar
                  data={firstTouchChart}
                  options={{
                    ...chartOptions(),
                    plugins: { ...chartOptions().plugins, legend: { display: false } },
                    indexAxis: 'y',
                  }}
                  height={220}
                />
              ) : (
                <EmptyState>Not enough conversion data yet</EmptyState>
              )}
            </ChartCard>

            <ChartCard>
              <ChartTitle>Top landing pages</ChartTitle>
              {data.landing_pages.length > 0 ? (
                <Table>
                  <thead>
                    <tr>
                      <Th>Path</Th>
                      <Th style={{ textAlign: 'right' }}>Sessions</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.landing_pages.map((lp, i) => (
                      <tr key={i}>
                        <Td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{lp.landing_path}</Td>
                        <Td style={{ textAlign: 'right', fontWeight: 600 }}>{Number(lp.sessions).toLocaleString()}</Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <EmptyState>No data yet</EmptyState>
              )}
            </ChartCard>

            {/* Recent conversions full-width */}
            <TableCard>
              <ChartTitle>Recent conversions — full journey</ChartTitle>
              <p style={{ margin: '0 0 1rem', fontSize: '0.82rem', color: '#1a3352', opacity: 0.7 }}>
                <strong>First touch</strong> = the source that originally brought this visitor to the site. &nbsp;
                <strong>Last touch</strong> = the source they came from in the session they actually booked. &nbsp;
                <strong>Pages</strong> = pages viewed in the booking session. &nbsp;
                <strong>Visits</strong> = total site visits before converting.
              </p>
              {data.recent_conversions.length > 0 ? (
                <div style={{ overflowX: 'auto' }}>
                  <Table>
                    <thead>
                      <tr>
                        <Th>Booking Ref</Th>
                        <Th>Type</Th>
                        <Th>Value</Th>
                        <Th>First Touch</Th>
                        <Th>Last Touch</Th>
                        <Th style={{ textAlign: 'center' }}>Pages</Th>
                        <Th style={{ textAlign: 'center' }}>Visits</Th>
                        <Th>Returning?</Th>
                        <Th>Date</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.recent_conversions.map((c, i) => {
                        const firstSrc = c.first_source || 'direct';
                        const lastSrc  = c.last_source  || 'direct';
                        const sameTouch = firstSrc === lastSrc && (c.first_medium || '') === (c.last_medium || '');
                        return (
                          <tr key={i}>
                            <Td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{c.booking_reference}</Td>
                            <Td>
                              <TypeBadge type={c.type}>
                                {c.type === 'membership_signup' ? 'Membership' : 'Ticket'}
                              </TypeBadge>
                            </Td>
                            <Td style={{ fontWeight: 600 }}>{c.value ? formatCurrency(c.value) : '—'}</Td>
                            <Td>
                              <span style={{ fontSize: '0.82rem' }}>
                                {firstSrc}{c.first_medium ? ` / ${c.first_medium}` : ''}
                              </span>
                              {c.first_campaign && (
                                <div style={{ fontSize: '0.74rem', opacity: 0.6 }}>{c.first_campaign}</div>
                              )}
                              {c.first_landing && (
                                <div style={{ fontSize: '0.72rem', opacity: 0.5, fontFamily: 'monospace' }}>{c.first_landing}</div>
                              )}
                            </Td>
                            <Td>
                              {sameTouch ? (
                                <span style={{ fontSize: '0.82rem', opacity: 0.5 }}>same as first</span>
                              ) : (
                                <span style={{ fontSize: '0.82rem' }}>
                                  {lastSrc}{c.last_medium ? ` / ${c.last_medium}` : ''}
                                  {c.last_campaign && (
                                    <div style={{ fontSize: '0.74rem', opacity: 0.6 }}>{c.last_campaign}</div>
                                  )}
                                </span>
                              )}
                            </Td>
                            <Td style={{ textAlign: 'center', fontWeight: 600 }}>{c.pages_in_session ?? '—'}</Td>
                            <Td style={{ textAlign: 'center', fontWeight: 600 }}>{c.sessions_total ?? '—'}</Td>
                            <Td>
                              {c.is_returning == null ? '—' : (
                                <TypeBadge type={c.is_returning == 1 ? 'membership_signup' : 'ticket_booking'}>
                                  {c.is_returning == 1 ? 'Return' : 'New'}
                                </TypeBadge>
                              )}
                            </Td>
                            <Td style={{ whiteSpace: 'nowrap' }}>{formatDate(c.converted_at)}</Td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <EmptyState>No conversions recorded yet — they will appear here after the first confirmed booking.</EmptyState>
              )}
            </TableCard>
          </ChartsGrid>
        </>
      )}

      {!loading && !error && !data && (
        <EmptyState>Select a date range above to load analytics data.</EmptyState>
      )}
    </Wrapper>
  );
};

export default MarketingAnalytics;
